import { loadProgress, weeklyPlayMinutes } from '../shared/progress';
import { FACTIONS } from '../shared/factions';
import { masteryPercent } from '../shared/student-kp';
import { friendlyKPName } from './data/kp-display-names';
import { renderBadgeDashboardPanel } from './badges/badges-ui';
import { appendC06Sections } from './parent/parent-dashboard-ui';
import { pageShell, academicButton } from './ui/components';
import { mountEscapeButton } from './escape-button';
import type { FactionId } from '../shared/types';

/**
 * 學術風格家長報表的「scoped」樣式 —— 只負責本頁特有的資料元件
 * (週摘要 stat 格、近 7 日柱狀圖、各派長條、列表)的紙感外觀。頁殼 / 標題 /
 * 按鈕沿用設計系統(`pageShell` + `academicButton` + `--ef-*` token),不在這裡
 * 重定義。一律走學術綠系,不用彩虹 / 遊戲彩光(boss 2026-06-08;家長端更要乾淨)。
 *
 * 冪等:用固定 id 守衛,重複 render 只注入一份。
 */
const PARENT_DASH_STYLE_ID = 'ef-parent-dashboard-style';
function ensureParentDashboardStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PARENT_DASH_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PARENT_DASH_STYLE_ID;
  style.textContent = `
.parent-dashboard { display: flex; flex-direction: column; gap: 22px; }
.parent-dashboard .parent-hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--ef-sub);
  background: var(--ef-note);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-sm);
  padding: 10px 14px;
}
/* 區塊標題:serif,綠色側標當紙的層次 */
.parent-dashboard h3 {
  margin: 4px 0 0;
  font-family: var(--ef-serif);
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.5px;
  color: var(--ef-ink);
  padding-left: 12px;
  border-left: 3px solid var(--ef-green);
}
.parent-dashboard > p { margin: 0; font-size: 14px; line-height: 1.6; color: var(--ef-ink-soft); }

/* 紙片區塊(本週摘要 / 30 天總覽等) */
.parent-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-card);
  padding: 18px 18px 20px;
}
.parent-section h3 { margin-top: 0; }

/* stat 方格:乾淨白底數字卡 */
.weekly-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
}
.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--ef-paper);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-sm);
  padding: 12px 14px;
}
.stat-label { font-size: 12px; letter-spacing: 0.5px; color: var(--ef-sub); }
.stat-value {
  font-family: var(--ef-serif);
  font-weight: 700;
  font-size: 22px;
  line-height: 1.1;
  color: var(--ef-green);
}

/* 近 7 日柱狀圖 —— 學術綠單色,克制 */
.dash-spark {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 120px;
  padding: 14px 16px 10px;
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-card);
}
.spark-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  min-width: 0;
}
.spark-bar {
  width: 60%;
  max-width: 28px;
  min-height: 3px;
  background: linear-gradient(180deg, var(--ef-green) 0%, var(--ef-green-dark) 100%);
  border-radius: 4px 4px 0 0;
}
.spark-label { font-size: 11px; color: var(--ef-sub); white-space: nowrap; }
.spark-val { font-size: 12px; font-weight: 700; color: var(--ef-ink-soft); }

/* 各派修煉值長條 —— 同樣綠系,門派 emoji 僅作小標 */
.dash-bars {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius);
  box-shadow: var(--ef-shadow-card);
}
.bar-row { display: flex; align-items: center; gap: 10px; }
.bar-emoji { font-size: 16px; width: 1.4em; text-align: center; flex: none; }
.bar-name { font-size: 13px; color: var(--ef-ink-soft); width: 64px; flex: none; }
.bar-track {
  flex: 1;
  height: 12px;
  background: var(--ef-paper);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-pill);
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--ef-green) 0%, var(--ef-green-dark) 100%);
  border-radius: var(--ef-radius-pill);
}
.bar-val {
  font-size: 13px;
  font-weight: 700;
  color: var(--ef-green);
  width: 44px;
  text-align: right;
  flex: none;
}

/* 弱 / 強單元等列表 —— 講義式清單 */
.dash-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.dash-list li {
  font-size: 14px;
  line-height: 1.5;
  color: var(--ef-ink-soft);
  background: var(--ef-paper-bright);
  border: 1px solid var(--ef-line);
  border-radius: var(--ef-radius-sm);
  padding: 10px 14px;
}
.dash-list li .moe-code {
  font-size: 11px;
  color: var(--ef-green-dark);
  background: rgba(31, 111, 92, 0.08);
  border-radius: var(--ef-radius-pill);
  padding: 1px 8px;
  margin-left: 4px;
}

/* 報表底部動作鈕橫排 */
.parent-dashboard .dash-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}
`;
  document.head.appendChild(style);
}

export function formatMastery(percent: number): string {
  return Number.isFinite(percent) ? `${percent}%` : '—';
}

/** Build weekly report data: XP per faction and total attempts for the past 7 days. */
export interface WeeklyReport {
  totalXP: number;
  totalAttempts: number;
  correctRate: number;          // 0-1
  factionXP: Record<FactionId, number>;
  /** ISO date strings YYYY-MM-DD for the 7-day window */
  dateRange: { from: string; to: string };
}

export function buildWeeklyReport(progress: { dailyXP?: Record<string, number>; studentKPs?: Array<{ totalAttempts: number; correctCount: number; lastReviewedISO: string }>; factionXP: Record<FactionId, number> }, nowISO: string = new Date().toISOString()): WeeklyReport {
  const today = nowISO.slice(0, 10);
  const days: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }

  const totalXP = days.reduce((sum, day) => sum + (progress.dailyXP?.[day] ?? 0), 0);

  // KPs reviewed in the past 7 days
  const recentKPs = (progress.studentKPs ?? []).filter(kp => {
    const rev = kp.lastReviewedISO?.slice(0, 10) ?? '';
    return rev >= days[0] && rev <= today;
  });
  const totalAttempts = recentKPs.reduce((s, kp) => s + kp.totalAttempts, 0);
  const totalCorrect  = recentKPs.reduce((s, kp) => s + kp.correctCount, 0);
  const correctRate   = totalAttempts > 0 ? totalCorrect / totalAttempts : 0;

  return {
    totalXP,
    totalAttempts,
    correctRate,
    factionXP: { ...progress.factionXP } as Record<FactionId, number>,
    dateRange: { from: days[0], to: today },
  };
}

export function renderParentDashboard(container: HTMLElement, onBack: () => void): void {
  container.innerHTML = '';
  ensureParentDashboardStyle();

  // 學術風格頁殼:serif 標題「家長中心」+ 固定「✕ 離開」鈕(經 onBack 確認框)。
  // 沿用 review-hub 慣例:自己掛 escape 並握住 unmount handle,點底部「回主畫面」
  // 時先卸載,避免堆疊兩顆離開鈕;報表內容塞進 shell.body。
  const shell = pageShell(container, {
    title: '家長中心',
    subtitle: '查看孩子的學習數據與遊戲時間。',
  });
  const unmountEscape = mountEscapeButton(() => { unmountEscape(); onBack(); });

  const root = document.createElement('div');
  root.className = 'parent-dashboard';
  shell.body.appendChild(root);

  const progress = loadProgress();
  if (!progress) {
    root.innerHTML = '<p>尚無進度資料。</p>';
    const back = academicButton({ label: '← 回主畫面', variant: 'secondary', onClick: () => { unmountEscape(); shell.root.remove(); onBack(); } });
    back.id = 'db-back';
    root.appendChild(back);
    return;
  }

  // 7-day sparkline data
  const last7Days: { date: string; xp: number }[] = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today); d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    last7Days.push({ date: key.slice(5), xp: progress.dailyXP?.[key] ?? 0 });
  }
  const maxXP = Math.max(1, ...last7Days.map(d => d.xp));

  // Weekly report
  const weeklyReport = buildWeeklyReport(progress);
  const weeklyXPTotal = last7Days.reduce((s, d) => s + d.xp, 0);

  // Faction bars
  const factionEntries = (['celestial', 'runic', 'techno', 'elemental', 'dominion'] as FactionId[])
    .map(id => ({ id, xp: progress.factionXP[id], faction: FACTIONS[id] }));
  const maxFactionXP = Math.max(1, ...factionEntries.map(e => e.xp));

  // Top weak / strong KPs
  const ranked = (progress.studentKPs ?? [])
    .filter(kp => kp.totalAttempts >= 3)
    .sort((a, b) => masteryPercent(a) - masteryPercent(b));
  const topWeak = ranked.slice(0, 3);
  const topStrong = ranked.slice(-3).reverse();

  // Play-time info from parentSettings — ALWAYS show today's minutes so the
  // parent can decide whether to set a limit (林媽媽 2026-05-21 fix #4).
  const dailyLimit = progress.parentSettings?.dailyTimeLimitMinutes ?? 0;
  const playedToday = progress.parentSettings?.minutesPlayedToday ?? 0;
  const timeLimitText = dailyLimit === 0
    ? `${playedToday} 分鐘 (無限制)`
    : `${playedToday} / ${dailyLimit} 分鐘`;
  // Weekly totals (林媽媽 fix #5).
  const weekMinutes = weeklyPlayMinutes(progress);
  const weekAverage = Math.round(weekMinutes / 7);

  // serif 主標由 pageShell 提供;此處不再放 <h1>。空狀態 / 補充說明改用
  // --ef-sub 灰階(不用遊戲青綠 #80cbc4 / #888),維持學術紙感。
  root.innerHTML = `
    <p class="parent-hint">本頁孩子看不到 (透過設定 ⚙️ 或主畫面「家長中心」進入)</p>

    <div class="parent-section weekly-report">
      <h3>📋 本週學習摘要 (${weeklyReport.dateRange.from} ~ ${weeklyReport.dateRange.to})</h3>
      <div class="weekly-stats">
        <div class="stat-item"><div class="stat-label">本週修煉值</div><div class="stat-value" id="weekly-xp">${weeklyXPTotal}</div></div>
        <div class="stat-item"><div class="stat-label">練習題數</div><div class="stat-value" id="weekly-attempts">${weeklyReport.totalAttempts}</div></div>
        <div class="stat-item"><div class="stat-label">答對率</div><div class="stat-value" id="weekly-correct-rate">${(weeklyReport.correctRate * 100).toFixed(0)}%</div></div>
        <div class="stat-item"><div class="stat-label">今日遊戲</div><div class="stat-value" id="play-time-today">${timeLimitText}</div></div>
        <div class="stat-item"><div class="stat-label">本週遊戲</div><div class="stat-value" id="play-time-week">${weekMinutes} 分鐘</div></div>
        <div class="stat-item"><div class="stat-label">每日平均</div><div class="stat-value" id="play-time-avg">${weekAverage} 分鐘</div></div>
      </div>
    </div>

    <h3>📊 近 7 日修煉值</h3>
    <div class="dash-spark">${last7Days.map(d =>
      `<div class="spark-col"><div class="spark-bar" style="height:${(d.xp / maxXP * 80).toFixed(1)}px"></div><div class="spark-label">${d.date}</div><div class="spark-val">${d.xp}</div></div>`
    ).join('')}</div>

    <h3>⚡ 各派修煉值</h3>
    <div class="dash-bars">${factionEntries.map(e =>
      `<div class="bar-row"><span class="bar-emoji">${e.faction.emoji}</span><span class="bar-name">${e.faction.displayName}</span><div class="bar-track"><div class="bar-fill" style="width:${(e.xp / maxFactionXP * 100).toFixed(0)}%"></div></div><span class="bar-val">${e.xp}</span></div>`
    ).join('')}</div>

    <h3>🔥 連續登入</h3>
    <p>${progress.streak?.days ?? 0} 天 (最近登入 ${progress.streak?.lastLoginISO?.slice(0, 10) ?? '-'})</p>

    <h3>⚠️ 最弱單元 (建議加強)</h3>
    <ul class="dash-list">${topWeak.length ? topWeak.map(kp =>
      `<li>${friendlyKPName(kp.kpId)} — 正確率 ${formatMastery(masteryPercent(kp))} (${kp.totalAttempts} 題)</li>`).join('') : '<li style="color:var(--ef-sub)">尚無資料</li>'}</ul>

    <h3>✅ 最強單元</h3>
    <ul class="dash-list">${topStrong.length ? topStrong.map(kp =>
      `<li>${friendlyKPName(kp.kpId)} — 正確率 ${formatMastery(masteryPercent(kp))} (${kp.totalAttempts} 題)</li>`).join('') : '<li style="color:var(--ef-sub)">尚無資料</li>'}</ul>

    <h3>📂 試卷上傳記錄</h3>
    <p style="color:var(--ef-sub);font-size:13px;">${(progress.studentKPs ?? []).filter(k => k.source === 'exam-upload').length} 筆來自試卷上傳</p>

    <div id="badge-dashboard-slot"></div>

    <div class="dash-actions"></div>
  `;
  // C04 Badge panel — 🏅 徽章成就 (家長感知學習成果). Mount AFTER innerHTML
  // so the placeholder slot exists. Wrapped in try/catch to keep dashboard
  // resilient if the catalogue/state read fails (e.g. partial migration).
  const badgeSlot = root.querySelector('#badge-dashboard-slot') as HTMLElement | null;
  if (badgeSlot) {
    try {
      renderBadgeDashboardPanel(badgeSlot, progress);
    } catch (err) {
      console.warn('[parent-dashboard] badge panel mount failed (non-fatal):', err);
    }
  }

  // 底部動作鈕 —— 學術按鈕(主 / 次)。id 與行為 (#db-back→onBack /
  // #db-academy-parent→ef:open-academy-parent) 全保留供測試 / wiring。
  const actions = root.querySelector('.dash-actions') as HTMLElement;
  // 林媽媽 2026-05-21 01:09 round-2 complaint: parentMode flag existed in
  // AcademyActions but no caller wired it. This button dispatches a global
  // event that main.ts listens for to swap into 'academy-parent' mode.
  const academyBtn = academicButton({
    label: '📚 查看修煉學院 (家長視角)',
    variant: 'primary',
    onClick: () => { unmountEscape(); shell.root.remove(); window.dispatchEvent(new CustomEvent('ef:open-academy-parent')); },
  });
  academyBtn.id = 'db-academy-parent';
  const backBtn = academicButton({
    label: '← 回主畫面',
    variant: 'secondary',
    onClick: () => { unmountEscape(); shell.root.remove(); onBack(); },
  });
  backBtn.id = 'db-back';
  actions.appendChild(academyBtn);
  actions.appendChild(backBtn);

  // C06 — append the 30-day summary + wrong-answer-driven weak-KP top-3
  // AFTER the existing weekly stats so the parent sees both windows side-
  // by-side. Kept as a separate function so future C06 iterations can
  // evolve without disturbing the 23-test legacy suite.
  appendC06Sections(root, progress);
}
